import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  ArrowRight, ShieldCheck, Lock, KeyRound, Server, Trash2, Download,
  Users, Plug, ChevronRight,
} from 'lucide-react';
import Seo from '../components/Seo';
import LandingHeader from '../components/LandingHeader';
import RelatedLinks from '../components/RelatedLinks';

const MEASURES = [
  { icon: Lock, title: 'Encryption in transit', desc: 'Every connection to MoneyFeed is served over HTTPS/TLS, so data moving between your device, WhatsApp and our servers is encrypted.' },
  { icon: Server, title: 'Encryption at rest', desc: 'Your data is stored in MongoDB Atlas, which encrypts stored data at rest and is hosted in managed, access-controlled infrastructure.' },
  { icon: KeyRound, title: 'Phone + PIN authentication', desc: 'Accounts are protected by phone-number verification and a personal PIN. PINs are stored only as a bcrypt hash — never in plain text.' },
  { icon: Users, title: 'Per-user data isolation', desc: 'Every record is scoped to its owner. API requests are authenticated and can only read or write the signed-in user’s own financial data.' },
  { icon: ShieldCheck, title: 'Abuse & fraud protection', desc: 'Login and verification endpoints are rate-limited, and inbound WhatsApp webhooks are signature-verified so only genuine messages are processed.' },
  { icon: Download, title: 'Export your data anytime', desc: 'You can export your transactions and receipts to CSV, Excel or PDF whenever you want — your data is never locked in.' },
];

const PROCESSORS = [
  { name: 'Twilio', role: 'Delivers and receives your WhatsApp messages.' },
  { name: 'Stripe', role: 'Processes subscription payments. MoneyFeed never stores your full card details.' },
  { name: 'Google Gemini / OpenAI', role: 'Powers AI categorization and receipt reading. Used to process the text and images you send.' },
  { name: 'MongoDB Atlas', role: 'Managed database that stores your account and financial records, encrypted at rest.' },
];

const FAQS = [
  { q: 'Is my financial data secure with MoneyFeed?', a: 'Yes. Data is encrypted in transit over HTTPS/TLS and stored in MongoDB Atlas, which encrypts data at rest. Access is protected by phone-number and PIN authentication, PINs are stored as bcrypt hashes, and every record is scoped to its owner so users can only access their own data.' },
  { q: 'How is my WhatsApp data handled?', a: 'Messages and receipt images you send are processed to extract your income, expenses and tax details, then stored against your account. MoneyFeed uses Twilio to send and receive WhatsApp messages and is not affiliated with WhatsApp or Meta.' },
  { q: 'Do you sell my data?', a: 'No. MoneyFeed does not sell your personal or financial information. Third-party services are used only to operate the product (messaging, payments, AI processing and hosting).' },
  { q: 'Can I delete my account and data?', a: 'Yes. You can delete your account from Settings at any time, which removes your data, and you can export your transactions and receipts beforehand if you want a copy.' },
  { q: 'How do you protect my payment details?', a: 'Subscription payments are processed by Stripe. Card details are handled by Stripe’s PCI-compliant systems; MoneyFeed does not store your full card number.' },
];

export default function Security() {
  const jsonLd = [
    { '@context': 'https://schema.org', '@type': 'WebPage', name: 'MoneyFeed Security', url: 'https://moneyfeed.io/security', description: 'How MoneyFeed protects your financial data: encryption in transit and at rest, phone + PIN authentication, per-user data isolation, data export and account deletion.' },
    { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: FAQS.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })) },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#ECFDF5] to-[#D1FAE5]" data-testid="security-page">
      <Seo
        title="MoneyFeed Security — How We Protect Your Data"
        description="How MoneyFeed keeps your financial data safe: HTTPS/TLS encryption, encryption at rest, phone + PIN authentication, per-user data isolation, and data export & deletion."
        path="/security"
        jsonLd={jsonLd}
      />
      <LandingHeader />

      <nav className="max-w-4xl mx-auto px-4 sm:px-8 pt-6 text-xs text-gray-500 flex items-center gap-1" aria-label="Breadcrumb" data-testid="security-breadcrumb">
        <Link to="/" className="hover:text-[#064E3B]">Home</Link>
        <ChevronRight className="h-3 w-3" /> <span className="text-[#064E3B] font-semibold">Security</span>
      </nav>

      <section className="max-w-3xl mx-auto px-4 sm:px-8 pt-8 pb-6 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#84CC16]/15 rounded-full mb-5">
          <ShieldCheck className="h-4 w-4 text-[#65A30D]" />
          <span className="text-xs font-bold text-[#65A30D] uppercase tracking-wider">Security & Data</span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold text-[#064E3B] leading-tight mb-5">Your financial data, protected</h1>
        <p className="text-base sm:text-lg text-gray-700 max-w-2xl mx-auto">
          MoneyFeed handles income, expenses and receipts, so security matters. Here’s exactly how your data is protected, who processes it, and the control you keep over it.
        </p>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {MEASURES.map((m) => (
            <div key={m.title} className="bg-white rounded-2xl shadow-md p-6" data-testid={`security-measure-${m.title}`}>
              <div className="w-11 h-11 rounded-xl bg-[#ECFDF5] flex items-center justify-center mb-4">
                <m.icon className="h-5 w-5 text-[#065F46]" />
              </div>
              <h2 className="font-bold text-[#064E3B] mb-1 text-base">{m.title}</h2>
              <p className="text-gray-600 text-sm">{m.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-8 py-8">
        <div className="bg-white rounded-2xl shadow-md p-7" data-testid="security-processors">
          <div className="flex items-center gap-2 mb-4">
            <Plug className="h-5 w-5 text-[#065F46]" />
            <h2 className="text-xl font-bold text-[#064E3B]">Third-party services we use</h2>
          </div>
          <p className="text-sm text-gray-600 mb-4">MoneyFeed relies on a small set of trusted providers to operate. Each processes only what it needs to:</p>
          <ul className="divide-y divide-gray-100">
            {PROCESSORS.map((p) => (
              <li key={p.name} className="py-3 flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3">
                <span className="font-bold text-[#064E3B] text-sm min-w-[180px]">{p.name}</span>
                <span className="text-gray-600 text-sm">{p.role}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-8 py-8">
        <div className="bg-white rounded-2xl shadow-md p-7 flex items-start gap-4" data-testid="security-control">
          <div className="w-11 h-11 rounded-xl bg-[#ECFDF5] flex items-center justify-center flex-shrink-0">
            <Trash2 className="h-5 w-5 text-[#065F46]" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-[#064E3B] mb-1">You stay in control</h2>
            <p className="text-gray-600 text-sm">
              You can export your data (CSV, Excel or PDF) or delete your account entirely from Settings at any time. For full details, read our{' '}
              <Link to="/privacy" className="text-[#065F46] font-semibold underline">Privacy Policy</Link>,{' '}
              <Link to="/data-protection" className="text-[#065F46] font-semibold underline">Data Protection</Link> page and{' '}
              <Link to="/terms" className="text-[#065F46] font-semibold underline">Terms</Link>. Security questions? Email{' '}
              <a href="mailto:feed@moneyfeed.io" className="text-[#065F46] font-semibold underline">feed@moneyfeed.io</a>.
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-8 py-12">
        <h2 className="text-3xl font-bold text-[#064E3B] text-center mb-10">Security FAQ</h2>
        <div className="space-y-4">
          {FAQS.map((f, i) => (
            <div key={i} className="bg-white rounded-2xl shadow-sm p-6" data-testid={`security-faq-${i}`}>
              <h3 className="font-bold text-[#064E3B] mb-2">{f.q}</h3>
              <p className="text-gray-600 text-sm">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-8 py-8">
        <div className="bg-[#064E3B] rounded-3xl p-10 text-center">
          <h2 className="text-3xl font-bold text-white mb-3">Bookkeeping you can trust</h2>
          <p className="text-[#A7F3D0] mb-6 max-w-xl mx-auto">Start free — your data stays encrypted, private and yours.</p>
          <Link to="/auth">
            <Button className="bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold rounded-full px-8 py-6 text-base" data-testid="security-cta-signup">
              Get started free <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </div>
      </section>

      <RelatedLinks />

      <footer className="max-w-6xl mx-auto px-4 sm:px-8 py-8 text-center">
        <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500">
          <Link to="/" className="hover:text-[#064E3B]">Home</Link>
          <Link to="/about-moneyfeed" className="hover:text-[#064E3B]">About</Link>
          <Link to="/features" className="hover:text-[#064E3B]">Features</Link>
          <Link to="/privacy" className="hover:text-[#064E3B]">Privacy</Link>
          <Link to="/data-protection" className="hover:text-[#064E3B]">Data Protection</Link>
        </div>
      </footer>
    </div>
  );
}
